import { NoteLabel } from '@seen/core/models/noteLabel'
import {
  VISIT_NOTE_CATEGORIES,
  visitNoteCategoryToken,
} from '@seen/core/models/visitNoteCategory'
import { type ReactNode, useCallback, useMemo } from 'react'
import { LabelCombobox } from '#src/components/LabelCombobox.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function NoteLabelCombobox({
  value,
  onChange,
  children,
  includeCategories = false,
}: {
  value: string[]
  onChange: (value: string[]) => any
  children: ReactNode
  /** Also offer the hard-coded visit-note categories as pick-only chips. */
  includeCategories?: boolean
}): ReactNode {
  const { data: labels } = useSearch(NoteLabel, NoteLabel.baseSearchQuery())
  const categories = useMemo(
    () =>
      includeCategories
        ? VISIT_NOTE_CATEGORIES.map((c) => ({
            value: visitNoteCategoryToken(c),
            color: c.color,
            name: c.display,
          }))
        : undefined,
    [includeCategories]
  )
  const createMutation = useCreateMutation<NoteLabel>()
  const createNoteLabel = createMutation.mutate
  const currentPractitioner = useCurrentPractitioner()
  const onCreate = useCallback(
    (name: string, color: string) => {
      createNoteLabel(
        new NoteLabel({
          name,
          color,
          author: currentPractitioner.reference,
        }),
        {
          onSuccess: (l) => {
            onChange([...value, l.reference.reference])
          },
        }
      )
    },
    [value, onChange, createNoteLabel, currentPractitioner.reference]
  )
  return (
    <LabelCombobox
      labels={labels}
      categories={categories}
      value={value}
      onChange={onChange}
      onCreate={onCreate}
      isCreating={createMutation.isPending}
    >
      {children}
    </LabelCombobox>
  )
}
